/**
 * Animated scenery props, in the game: passes/kitProps.ts makes each placed prop from its loop's frame 0
 * (`animStill`) and hands it to `propWatcher`; this file's pass then points each watched prop's image at its loop's
 * frame for the moment (a global clock, a phase from where it stands so neighbours do not move in step). Only an image
 * swap per prop on screen whose frame changed: the frames are baked once per prop id (kit/propAnim.ts) and go through
 * the same master-look / true-pixel finish as the stills, then sit in the texture atlas like any sprite. Still while
 * the ambience is off (the low tiers' preset). Off (stills only) with `localStorage['c0.propAnim'] = '0'`.
 */
import { hash2 } from '@nine/shared';
import { settings } from '../../settings';
import { masterLookImg } from '../masterLook';
import { registerRenderPass } from '../passes/registry';
import type { Sprite } from '../scenery';
import type { Ground, Prop } from '../terrain';
import { truePxImg } from '../truepx/page';
import { propLoop } from './propAnim';
import './propAnim4';
import './propAnimSpecs';
import { propSprite, type PropId } from './props';

interface Live {
  readonly prop: Prop;
  readonly frames: readonly HTMLCanvasElement[];
  readonly period: number;
  readonly phase: number;
  k: number;
}
const LIVE = new WeakMap<Ground, Live[]>();

/** The sprite a placed prop is made from: its loop's (padded) frame 0 when it has a loop, else its still. */
export function animStill(id: PropId): Sprite {
  const s = propSprite(id);
  return propLoop(id, s) ?? s;
}

/**
 * Starts a camera angle's watch list afresh (its scenery is being built) and returns the call that adds each placed
 * prop to it: `id` the prop's id, (`x`, `y`) where it stands (tiles), for its phase.
 */
export function propWatcher(ground: Ground): (prop: Prop, id: PropId, x: number, y: number) => void {
  const list: Live[] = [];
  LIVE.set(ground, list);
  if (typeof localStorage !== 'undefined' && localStorage.getItem('c0.propAnim') === '0') return () => {};
  return (prop, id, x, y) => {
    const loop = propLoop(id, propSprite(id));
    if (!loop) return;
    const frames = loop.frames.map((f) => truePxImg(masterLookImg(f)));
    prop.img = frames[0]!;
    list.push({ prop, frames, period: loop.period, phase: hash2(Math.round(x * 8), Math.round(y * 8) + 977), k: 0 });
  };
}

registerRenderPass<{ t: number }>({
  id: 'propAnim',
  layer: 'ground',
  order: 512,
  state: () => ({ t: 0 }),
  draw(f, s) {
    const list = f.outdoors && LIVE.get(f.outdoors.ground);
    if (!list?.length) return;
    s.t += Math.min(f.dt, 0.1);
    const on = settings.ambience;
    // Off screen nothing changes (the camera turned, props are lifted: no cull then).
    const cull = !f.iso.warped;
    const x0 = f.visible.x - f.ox - 8;
    const y0 = f.visible.y - f.oy - 8;
    const x1 = x0 + f.visible.w + 16;
    const y1 = y0 + f.visible.h + 16;
    for (const e of list) {
      const p = e.prop;
      if (on && cull && (p.x > x1 || p.y > y1 || p.x + p.img.width < x0 || p.y + p.img.height < y0)) continue;
      const n = e.frames.length;
      const k = on ? Math.floor((((s.t / e.period + e.phase) % 1) + 1) % 1 * n) % n : 0;
      if (k === e.k) continue;
      e.k = k;
      p.img = e.frames[k]!;
    }
  },
});
